/**
 * Funnel Service - Firestore operations for funnel management
 */

import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';

const FUNNELS_COLLECTION = 'funnels';

/**
 * Create a new funnel
 */
export const createFunnel = async (funnelData) => {
    try {
        // Remove id from funnelData before saving (it will be auto-generated)
        const { id, ...dataToSave } = funnelData;
        const docRef = await addDoc(collection(db, FUNNELS_COLLECTION), {
            ...dataToSave,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
        return { id: docRef.id, ...dataToSave };
    } catch (error) {
        console.error('Error creating funnel:', error);
        throw error;
    }
};

/**
 * Get a funnel by ID
 */
export const getFunnel = async (funnelId) => {
    try {
        const docRef = doc(db, FUNNELS_COLLECTION, funnelId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        }
        return null;
    } catch (error) {
        console.error('Error getting funnel:', error);
        throw error;
    }
};

/**
 * Get all funnels for a brand
 */
export const getFunnelsByBrand = async (brandId) => {
    try {
        const q = query(
            collection(db, FUNNELS_COLLECTION),
            where('brandId', '==', brandId),
            orderBy('updatedAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting funnels by brand:', error);
        throw error;
    }
};

/**
 * Get funnels by platform
 */
export const getFunnelsByPlatform = async (brandId, platform) => {
    try {
        const q = query(
            collection(db, FUNNELS_COLLECTION),
            where('brandId', '==', brandId),
            where('platform', '==', platform),
            orderBy('updatedAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting funnels by platform:', error);
        throw error;
    }
};

/**
 * Update a funnel
 */
export const updateFunnel = async (funnelId, updates) => {
    try {
        // Remove id from updates (document ID is separate from document data)
        const { id, ...dataToUpdate } = updates;
        const docRef = doc(db, FUNNELS_COLLECTION, funnelId);
        await updateDoc(docRef, {
            ...dataToUpdate,
            updatedAt: serverTimestamp()
        });
        return { id: funnelId, ...dataToUpdate };
    } catch (error) {
        console.error('Error updating funnel:', error);
        throw error;
    }
};

/**
 * Save funnel (create or update)
 */
export const saveFunnel = async (funnelData) => {
    if (funnelData.id) {
        return updateFunnel(funnelData.id, funnelData);
    }
    return createFunnel(funnelData);
};

/**
 * Delete a funnel
 */
export const deleteFunnel = async (funnelId) => {
    try {
        await deleteDoc(doc(db, FUNNELS_COLLECTION, funnelId));
        return true;
    } catch (error) {
        console.error('Error deleting funnel:', error);
        throw error;
    }
};

/**
 * Duplicate a funnel
 */
export const duplicateFunnel = async (funnelId, newName = null) => {
    try {
        const original = await getFunnel(funnelId);
        if (!original) {
            throw new Error('Funnel not found');
        }

        const duplicate = {
            ...original,
            name: newName || `${original.name} (Copy)`,
            status: 'draft'
        };
        delete duplicate.id;

        return createFunnel(duplicate);
    } catch (error) {
        console.error('Error duplicating funnel:', error);
        throw error;
    }
};

/**
 * Export funnel data for pushing to ad platform
 */
export const exportFunnelForPush = (funnel) => {
    const campaigns = [];
    const adSets = [];
    const ads = [];

    // Extract campaigns
    funnel.nodes.filter(n => n.type === 'campaign').forEach(node => {
        campaigns.push({
            id: node.id,
            name: node.data.name,
            objective: node.data.objective,
            budget: node.data.budget,
            schedule: node.data.schedule,
            funnelStage: node.data.funnelStage,
            status: node.data.status
        });
    });

    // Extract ad sets with parent campaign mapping
    funnel.nodes.filter(n => n.type === 'adset').forEach(node => {
        // Find parent campaign from edges
        const parentEdge = funnel.edges.find(e => e.target === node.id);
        const parentCampaignId = parentEdge?.source;

        adSets.push({
            id: node.id,
            campaignId: parentCampaignId,
            name: node.data.name,
            targeting: node.data.targeting,
            schedule: node.data.schedule,
            bidStrategy: node.data.bidStrategy,
            optimizationGoal: node.data.optimizationGoal
        });
    });

    // Extract ads with parent ad set mapping
    funnel.nodes.filter(n => n.type === 'ad').forEach(node => {
        // Find parent ad set from edges
        const parentEdge = funnel.edges.find(e => e.target === node.id);
        const parentAdSetId = parentEdge?.source;

        ads.push({
            id: node.id,
            adSetId: parentAdSetId,
            name: node.data.name,
            creativeId: node.data.creativeId,
            headline: node.data.headline,
            primaryText: node.data.primaryText,
            cta: node.data.cta,
            destinationUrl: node.data.destinationUrl,
            variant: node.data.variant
        });
    });

    return {
        funnelId: funnel.id,
        funnelName: funnel.name,
        platform: funnel.platform,
        campaigns,
        adSets,
        ads
    };
};

/**
 * Validate funnel structure before pushing
 */
export const validateFunnelForPush = (funnel) => {
    const errors = [];
    const warnings = [];

    // Check for at least one campaign
    const campaigns = funnel.nodes.filter(n => n.type === 'campaign');
    if (campaigns.length === 0) {
        errors.push('Funnel must have at least one campaign');
    }

    // Check each campaign has at least one ad set
    campaigns.forEach(campaign => {
        const campaignAdSets = funnel.edges
            .filter(e => e.source === campaign.id)
            .map(e => funnel.nodes.find(n => n.id === e.target))
            .filter(n => n?.type === 'adset');

        if (campaignAdSets.length === 0) {
            errors.push(`Campaign "${campaign.data.name}" has no ad sets`);
        }

        // Check budget
        if (!campaign.data.budget?.amount || campaign.data.budget.amount <= 0) {
            errors.push(`Campaign "${campaign.data.name}" has no budget set`);
        }
    });

    // Check each ad set has at least one ad
    const adSets = funnel.nodes.filter(n => n.type === 'adset');
    adSets.forEach(adSet => {
        const adSetAds = funnel.edges
            .filter(e => e.source === adSet.id)
            .map(e => funnel.nodes.find(n => n.id === e.target))
            .filter(n => n?.type === 'ad');

        if (adSetAds.length === 0) {
            warnings.push(`Ad Set "${adSet.data.name}" has no ads`);
        }
    });

    // Check ads have creatives
    const ads = funnel.nodes.filter(n => n.type === 'ad');
    ads.forEach(ad => {
        if (!ad.data.creativeId) {
            errors.push(`Ad "${ad.data.name}" has no creative selected`);
        }
        if (!ad.data.headline) {
            warnings.push(`Ad "${ad.data.name}" has no headline`);
        }
    });

    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
};

export default {
    createFunnel,
    getFunnel,
    getFunnelsByBrand,
    getFunnelsByPlatform,
    updateFunnel,
    saveFunnel,
    deleteFunnel,
    duplicateFunnel,
    exportFunnelForPush,
    validateFunnelForPush
};
